<script setup>
import ArrowDownIcon from '@iconify-vue/ph/arrow-down';
import ArrowUpIcon from '@iconify-vue/ph/arrow-up';
import { AnimatePresence, Motion } from 'motion-v';
import {
    TooltipContent,
    TooltipPortal,
    TooltipProvider,
    TooltipRoot,
    TooltipTrigger,
} from 'reka-ui';
import { inject, ref } from 'vue';

const {
    header,
    title,
    align = 'left',
    width = null,
    icon,
} = defineProps({
    header: {
        type: Object,
        required: true,
    },
    title: {
        type: String,
        required: true,
    },
    align: {
        type: String,
        required: false,
    },
    width: {
        type: Number,
        required: false,
    },
    icon: {
        type: Object,
        required: false,
    },
});

const isTooltipOpen = ref(false);

const pinnedRight = inject('pinnedRight', ref({ offsets: {}, firstId: null }));
</script>

<template>
    <th
        class="p-0"
        :data-column-id="header.column.id"
        :class="{
            'cursor-pointer': header.column.getCanSort(),
            'sticky bg-white': header.column.getIsPinned(),
            'before:pointer-events-none before:absolute before:top-0 before:-left-[5px] before:block before:h-full before:w-[5px] before:bg-gradient-to-l before:from-gray-200 before:to-gray-50 before:opacity-100 before:content-[\'\']':
                header.column.getIsPinned() &&
                pinnedRight.firstId === header.column.id,
        }"
        @click="(event) => header.column.getToggleSortingHandler()?.(event)"
        :style="{
            width: `${width}px`,
            right: header.column.getIsPinned()
                ? `${pinnedRight.offsets[header.column.id] ?? 0}px`
                : undefined,
        }"
    >
        <div
            class="inline-flex h-12 w-full items-center gap-1 border-t border-b border-gray-200 px-4 py-2"
            :class="{
                'justify-start text-left': align === 'left',
                'justify-center text-center': align === 'center',
                'justify-end text-right': align === 'right',
            }"
        >
            <TooltipProvider v-if="icon">
                <TooltipRoot v-model:open="isTooltipOpen" :delay-duration="0">
                    <TooltipTrigger as-child>
                        <component :is="icon" class="size-6" />
                    </TooltipTrigger>

                    <TooltipPortal>
                        <AnimatePresence>
                            <TooltipContent
                                v-if="isTooltipOpen"
                                as-child
                                force-mount
                                side="top"
                                :side-offset="5"
                            >
                                <Motion
                                    :initial="{ opacity: 0, scale: 0.96 }"
                                    :animate="{ opacity: 1, scale: 1 }"
                                    :exit="{ opacity: 0, scale: 0.96 }"
                                    :transition="{ duration: 0.15 }"
                                    class="z-50 rounded-sm border border-gray-300 bg-white px-5 py-2 text-sm shadow-sm"
                                    v-html="title"
                                />
                            </TooltipContent>
                        </AnimatePresence>
                    </TooltipPortal>
                </TooltipRoot>
            </TooltipProvider>
            <div v-else v-html="title" class="whitespace-nowrap"></div>

            <div v-if="header.column.getCanSort()" class="inline-flex">
                <ArrowUpIcon
                    class="h-6 w-3"
                    :class="{
                        'opacity-100': header.column.getIsSorted() === 'asc',
                        'opacity-40': header.column.getIsSorted() !== 'asc',
                    }"
                />

                <ArrowDownIcon
                    class="h-6 w-3"
                    :class="{
                        'opacity-100': header.column.getIsSorted() === 'desc',
                        'opacity-40': header.column.getIsSorted() !== 'desc',
                    }"
                />
            </div>
        </div>
    </th>
</template>
